import {
    AppBar,
    Box,
    Button,
    Collapse,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Toolbar,
    Typography,
    Avatar,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Link as RouterLink, useLocation } from "react-router-dom";
import ThemeToggleButton from "./ThemeToggleButton";
import SearchBar from "./search/SearchBar";
import { useAuth } from "../hooks/useAuth";
import LogoutButton from "./LogoutButton";
import { useState } from "react";
import KofiLinkButton from "./KofiLinkButton";

const NavBar = () => {
    const [drawerOpen, setDrawerOpen] = useState(false);
    // const [guidesOpen, setGuidesOpen] = useState(false);
    const [adminOpen, setAdminOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const location = useLocation();
    const isHome = location.pathname === "/";
    const theme = useTheme();
    const { isAdmin, isAuthenticated, user } = useAuth();

    const handleDrawerToggle = () => setDrawerOpen(!drawerOpen);
    // const handleGuidesToggle = () => setGuidesOpen(!guidesOpen);
    const handleAdminToggle = () => setAdminOpen(!adminOpen);

    const desktopNavLinks = [
        { label: "home", to: "/" },
        { label: "resources", to: "/resource-hub" },
        { label: "faq", to: "/faq" },
        { label: "contact", to: "/contact" },
    ];

    const adminLinks = [
        { label: "admin panel", to: "/admin" },
        { label: "add items", to: "/admin/addItems" },
        { label: "edit items", to: "/admin/editItems" },
        { label: "edit single item", to: "/admin/editSingleItem" },
        { label: "add trades", to: "/admin/addTrades" },
        { label: "edit trades", to: "/admin/editTrades" },
        { label: "mark rr", to: "/admin/markRR" },
        { label: "change value", to: "/admin/changeValue" },
        { label: "make petpage", to: "/admin/makePetpage" },
        { label: "migrate data", to: "/admin/migrateData" },
        { label: "update userscript", to: "/admin/updateUserscript" },
    ];

    return (
        <>
            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    backgroundColor: theme.palette.background.secondPaper,
                    color: theme.palette.text.primary,
                    borderBottom: "3px solid",
                    borderColor: theme.palette.accent.main,
                }}
            >
                <Toolbar
                    sx={{
                        px: 2,
                        py: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        position: "relative",
                    }}
                >
                    {/* ---------------- Mobile Layout ---------------- */}
                    <Box
                        sx={{
                            display: { xs: "flex", sm: "none" },
                            alignItems: "center",
                            gap: 1,
                            width: "100%",
                        }}
                    >
                        <IconButton
                            edge="start"
                            color="inherit"
                            aria-label="menu"
                            onClick={handleDrawerToggle}
                        >
                            <MenuIcon />
                        </IconButton>
                        {!isHome && (
                            <Box sx={{ flexGrow: 1 }}>
                                <SearchBar fullWidth iconColor={theme.palette.accent.main} />
                            </Box>
                        )}
                    </Box>

                    {/* ---------------- Desktop Layout ---------------- */}
                    <Box
                        sx={{
                            display: { xs: "none", sm: "flex" },
                            alignItems: "center",
                            width: "100%",
                        }}
                    >
                        {searchOpen ? (
                            // Search Mode
                            <>
                                <IconButton onClick={() => setSearchOpen(false)}>
                                    <ArrowBackIcon sx={{ color: theme.palette.accent.main }} />
                                </IconButton>
                                <Box sx={{ flexGrow: 1, mx: 2 }}>
                                    <SearchBar fullWidth iconColor={theme.palette.accent.main} />
                                </Box>
                                <ThemeToggleButton />
                            </>
                        ) : (
                            // Normal Mode
                            <>
                                {/* Left nav links */}
                                <Box sx={{ display: "flex", gap: 2 }}>
                                    {desktopNavLinks.map(({ label, to }) => (
                                        <Button key={label} color="inherit" component={RouterLink} to={to}>
                                            {label}
                                        </Button>
                                    ))}

                                    {isAuthenticated && isAdmin && (
                                        <Button color="inherit" component={RouterLink} to="/admin">
                                            Admin
                                        </Button>
                                    )}
                                </Box>

                                {/* Search bar placement */}
                                {!isHome && (
                                    <>
                                        {/* Full screen (lg+) → centered fixed width */}
                                        <Box
                                            sx={{
                                                display: { xs: "none", lg: "block" },
                                                position: "absolute",
                                                left: "50%",
                                                transform: "translateX(-50%)",
                                                width: "420px",
                                            }}
                                        >
                                            <SearchBar fullWidth={false} iconColor={theme.palette.accent.main} />
                                        </Box>

                                        {/* Medium desktop (md only) → fill available space between links and icons */}
                                        <Box
                                            sx={{
                                                display: { xs: "none", md: "flex", lg: "none" },
                                                flexGrow: 1,
                                                mx: 2,
                                            }}
                                        >
                                            <SearchBar fullWidth iconColor={theme.palette.accent.main} />
                                        </Box>
                                    </>
                                )}

                                {/* Right side controls */}
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
                                    {isAuthenticated && user && (
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mr: 2 }}>
                                            <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                                                {user.username}
                                            </Typography>
                                            <Avatar
                                                alt="User Avatar"
                                                src={
                                                    user.avatar
                                                        ? `https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.png`
                                                        : `https://cdn.discordapp.com/embed/avatars/0.png`
                                                }
                                                sx={{ width: 32, height: 32 }}
                                            />
                                        </Box>
                                    )}
                                    {isAuthenticated && isAdmin && <LogoutButton />}
                                    {!isHome && (
                                        <IconButton
                                            sx={{ display: { xs: "none", sm: "inline-flex", md: "none" } }}
                                            onClick={() => setSearchOpen(true)}
                                        >
                                            <SearchIcon sx={{ color: theme.palette.accent.main }} />
                                        </IconButton>
                                    )}
                                    <KofiLinkButton />
                                    <ThemeToggleButton />
                                </Box>
                            </>
                        )}
                    </Box>
                </Toolbar>
            </AppBar>

            {/* ---------------- Mobile Drawer ---------------- */}
            <Drawer anchor="left" open={drawerOpen} onClose={handleDrawerToggle}>
                <Box sx={{ width: 250 }} role="presentation">
                    <List>
                        <ListItem disablePadding>
                            <ListItemButton component={RouterLink} to="/" onClick={handleDrawerToggle}>
                                <ListItemText primary="Home" />
                            </ListItemButton>
                        </ListItem>

                           <ListItem disablePadding>
                            <ListItemButton component={RouterLink} to="/resource-hub" onClick={handleDrawerToggle}>
                                <ListItemText primary="Resource Hub" />
                            </ListItemButton>
                        </ListItem>

                        <ListItem disablePadding>
                            <ListItemButton component={RouterLink} to="/faq" onClick={handleDrawerToggle}>
                                <ListItemText primary="FAQ" />
                            </ListItemButton>
                        </ListItem>

                        <ListItem disablePadding>
                            <ListItemButton component={RouterLink} to="/contact" onClick={handleDrawerToggle}>
                                <ListItemText primary="Contact" />
                            </ListItemButton>
                        </ListItem>

                        <ListItem disablePadding>
                            <ListItemButton component={RouterLink} to="/about-us" onClick={handleDrawerToggle}>
                                <ListItemText primary="About Us" />
                            </ListItemButton>
                        </ListItem>
                        
                        <ListItem disablePadding>
                            <ListItemButton component={RouterLink} to="/privacy" onClick={handleDrawerToggle}>
                                <ListItemText primary="Privacy Policy" />
                            </ListItemButton>
                        </ListItem>

                        <ListItem disablePadding>
                            <ListItemButton component={RouterLink} to="/terms-of-service" onClick={handleDrawerToggle}>
                                <ListItemText primary="Terms of Service" />
                            </ListItemButton>
                        </ListItem>

                        <ListItem disablePadding>
                            <ListItemButton component={RouterLink} to="/site-map" onClick={handleDrawerToggle}>
                                <ListItemText primary="Site Map" />
                            </ListItemButton>
                        </ListItem>
                        
                        <ListItem disablePadding>
                            <ListItemButton component={RouterLink} to="/support" onClick={handleDrawerToggle}>
                                <ListItemText primary="Supporting Stylisher" />
                            </ListItemButton>
                        </ListItem>

                        {isAdmin && (
                            <>
                                <ListItemButton onClick={handleAdminToggle}>
                                    <ListItemText primary="Admin Tools" />
                                    {adminOpen ? <ExpandLess /> : <ExpandMore />}
                                </ListItemButton>
                                <Collapse in={adminOpen} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding>
                                        {adminLinks.map(({ label, to }) => (
                                            <ListItemButton
                                                key={label}
                                                sx={{ pl: 4 }}
                                                component={RouterLink}
                                                to={to}
                                                onClick={handleDrawerToggle}
                                            >
                                                <ListItemText primary={label} />
                                            </ListItemButton>
                                        ))}
                                    </List>
                                </Collapse>
                            </>
                        )}

                        {isAuthenticated && isAdmin && (
                            <ListItem disablePadding>
                                <ListItemButton
                                    onClick={() => {
                                        handleDrawerToggle();
                                    }}
                                >
                                    <ListItemText primary="Logout" />
                                </ListItemButton>
                            </ListItem>
                        )}
                    </List>

                    <Divider />

                    <Box sx={{ p: 2 }}>
                        <ThemeToggleButton />
                    </Box>
                </Box>
            </Drawer>
        </>
    );
};

export default NavBar;
